import React, { useEffect, useState } from "react";
import { Controller, useController } from "react-hook-form";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import i18next from "i18next";
import {
  BUTTON_CONNECT_ACCOUNT,
  FIELD_REQUIRED,
} from "../../i18n/keysTranslations";
import { getReactHookFormNestedError } from "../../utils/errors";
import { useSelectorApp } from "../../lib/redux";
import { CANVA } from "../../utils/constants";
import { useTranslationApp } from "../../lib/i18next";
import AddIcon from "@mui/icons-material/Add";
import useButtonActionPlatform from "../../hooks/useButtonActionPlatform";
import { getUniqueID } from "../../utils/numbers";
import { INPUT_COLOR_BORDER } from "../../utils/colors";

const filter = createFilterOptions();
const ID_ADD = "Add Account";

const SelectCanvaAccountWithConnect = ({
  control,
  errors = {},
  rules = {
    required: {
      value: true,
      message: i18next.t(FIELD_REQUIRED),
    },
  },
  name,
  label,
  disabled = false,
  placeholder,
  variant = "filled",
  fullWidth,
  helperText,
  onChangeAction = () => {},
  validationChange = async () => true,
  sxTextField = {},
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [id] = useState(getUniqueID());
  const [connectingAccount, setConnectingAccount] = useState(false);

  const {
    field: { value: fieldValue, onChange: onFieldChange },
  } = useController({
    name,
    control,
  });

  const error = getReactHookFormNestedError(errors, name);
  const { t } = useTranslationApp();
  const BUTTON_ACTIONS_COMPONENTS = useButtonActionPlatform();

  const { isLoading, integrations } = useSelectorApp((state) => state.user);

  const onClickConnectAccount = async () => {
    setConnectingAccount(true);
    const element = document.getElementById(id);
    element?.click();
    return;
  };

  useEffect(() => {
    const canvaIntegrations = integrations?.canva || {};
    const canvaOptions = Object.keys(canvaIntegrations)
      .filter((key) => {
        const integration = canvaIntegrations[key];
        return integration?.isActive === true;
      })
      .map((key) => {
        const integration = canvaIntegrations[key];
        return {
          id: integration.id,
          name: integration.name || "",
          integrationID: integration.id,
        };
      });
    setOptions(canvaOptions);
  }, [integrations]);

  useEffect(() => {
    if (!fieldValue && options.length > 0) {
      const firstOption = options[0];
      onFieldChange(firstOption);
      onChangeAction(firstOption);
    }
    // eslint-disable-next-line
  }, [options]);

  if (isLoading) return null;
  return (
    <>
      {React.cloneElement(BUTTON_ACTIONS_COMPONENTS[CANVA], {
        id,
        text: BUTTON_CONNECT_ACCOUNT,
        mode: "add",
        icon: <AddIcon />,
        showButtonIcon: true,
        showLogo: false,
        onClick: () => {
          setConnectingAccount(false);
          setOpen(true);
        },
        onCallbackOpenPopup: () => {
          setConnectingAccount(false);
        },
        onCallbackCancel: () => {
          setConnectingAccount(false);
        },
        sx: {
          display: "none",
          justifyContent: "flex-start",
          background: "transparent",
          color: "inherit",
          m: 0,
          ml: -1,
          py: 1,
          px: 2,
          fontSize: "1rem",
          fontWeight: 500,
          "&:hover": {
            background: "transparent",
          },
        },
      })}
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field }) => (
          <Autocomplete
            {...field}
            loading={connectingAccount}
            id={name}
            hasLabel={Boolean(label)}
            value={options?.length > 0 ? field.value : ""}
            fullWidth={fullWidth}
            disabled={disabled}
            open={open}
            onOpen={() => {
              setOpen(true);
            }}
            onClose={() => {
              setOpen(false);
            }}
            onChange={async (_, value) => {
              const isValid = await validationChange(value);
              if (!isValid) {
                return;
              }
              if (value?.id === ID_ADD) {
                onClickConnectAccount();
                return;
              }

              field.onChange(value);
              onChangeAction(value);
            }}
            isOptionEqualToValue={(option, value) => {
              if (value?.id) {
                return option?.id === value?.id;
              }
              return option?.id === value;
            }}
            disableClearable={true}
            getOptionLabel={(option) => {
              if (typeof option === "string") {
                const optionValue = options.find((opt) => opt.id === option);
                return optionValue?.name || option;
              }
              return option?.name || option;
            }}
            options={options}
            filterOptions={(options, params) => {
              const filtered = filter(options, params);
              filtered.unshift({
                id: ID_ADD,
                name: t(BUTTON_CONNECT_ACCOUNT),
              });

              return filtered;
            }}
            renderOption={(props, option) => {
              return (
                <Box
                  {...props}
                  component="li"
                  key={`${option.id}-${option.integrationID}`}
                  sx={{
                    display: "flex",
                    gap: 0.5,
                  }}
                >
                  {option.id === ID_ADD && (
                    <AddIcon
                      sx={{
                        ml: -0.5,
                        color: "background.paperContrastText",
                      }}
                    />
                  )}
                  <Typography
                    variant="body1"
                    sx={{ color: "background.paperContrastText" }}
                  >
                    {option.name}
                  </Typography>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                hiddenLabel={!label}
                label={label}
                variant={variant}
                placeholder={field?.value?.length > 0 ? "" : placeholder}
                error={!!error}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {connectingAccount ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                helperText={
                  <Box
                    component="span"
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <span>{!!error ? error?.message : helperText}</span>
                  </Box>
                }
                sx={{
                  backgroundColor: "fields.background",
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                  ...sxTextField,
                  ".MuiFormHelperText-root": {
                    ml: "0 !important",
                  },
                }}
              />
            )}
          />
        )}
      />
    </>
  );
};

export default SelectCanvaAccountWithConnect;
